import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import {captureException} from '@sentry/browser';

import {ReactNode, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {isEnoughStorageAvailable} from 'shared/web';

import {createRehydrateThunk} from '../thunks/createRehydrateThunk';
import {GenericRTKQState} from '../types/GenericRTKQState';
import {deviceErrorUtils} from '../utils/deviceErrorUtils';

interface PersistGateProps {
  children: ReactNode;
  reducers: Record<string, unknown>;
  loadingComponent?: ReactNode;
}

function initializeCache(
  dispatch: ThunkDispatch<GenericRTKQState, unknown, UnknownAction>,
  reducers: Record<string, unknown>
) {
  try {
    // Find reducers that have a '___persistConfig' property
    const persistableSlices = Object.entries(reducers).filter(([_, reducer]) =>
      // @ts-expect-error - filter ensures this property exists
      Object.hasOwn(reducer, '___persistConfig')
    );
    if (persistableSlices.length === 0) {
      return Promise.resolve();
    }

    return Promise.all(
      persistableSlices.map(([path, reducer]) => {
        // @ts-expect-error - filter ensures this property exists
        const persistConfig = reducer.___persistConfig;
        const thunk = createRehydrateThunk({
          reducerPaths: [path],
          transforms: persistConfig.transforms || [],
          knownEndpointNames: persistConfig.knownEndpointNames,
          onError: (error: unknown) => {
            const errorInfo = deviceErrorUtils.analyzeDeviceError(error);

            // Always log to console for debugging
            console.error(`[PersistGate] Error rehydrating slice "${path}":`, error);

            if (errorInfo.isDeviceError) {
              console.warn(`[PersistGate] Device limitation detected: ${errorInfo.category}`);
              if (errorInfo.details.detectionReason) {
                console.warn(`[PersistGate] Reason: ${errorInfo.details.detectionReason}`);
              }
            }

            // Only log to Sentry if it's an actionable application error
            if (errorInfo.shouldLogToSentry) {
              captureException(error, {
                contexts: {
                  persistGate: {
                    operation: 'rehydrate',
                    slice: path,
                    errorCategory: errorInfo.category,
                    ...errorInfo.details,
                  },
                },
              });
            }
          },
        })();
        return dispatch(thunk);
      })
    );
  } catch (error) {
    const errorInfo = deviceErrorUtils.analyzeDeviceError(error);

    console.error('[PersistGate] Error during initializeCache setup:', error);

    if (errorInfo.isDeviceError) {
      console.warn(`[PersistGate] Device limitation detected: ${errorInfo.category}`);
      if (errorInfo.details.detectionReason) {
        console.warn(`[PersistGate] Reason: ${errorInfo.details.detectionReason}`);
      }
    }

    if (errorInfo.shouldLogToSentry) {
      captureException(error, {
        contexts: {
          persistGate: {
            operation: 'initializeSetup',
            errorCategory: errorInfo.category,
            ...errorInfo.details,
          },
        },
      });
    }
    return Promise.reject(error);
  }
}

const MIN_REQUIRED_BYTES = 15 * 1024 * 1024; // Require at least 15MB free
const TIMEOUT_MS = 2500; // Timeout for rehydration

export function PersistGate(props: PersistGateProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const dispatch = useDispatch();
  const hasInitAttempted = useRef(false);

  const {reducers, children, loadingComponent} = props;

  useEffect(
    function initializePersistedCache() {
      if (hasInitAttempted.current) {
        return;
      }
      hasInitAttempted.current = true;

      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const initializationLogic = async () => {
        try {
          const isEnoughSpace = await isEnoughStorageAvailable(MIN_REQUIRED_BYTES);
          if (!isEnoughSpace) {
            console.warn('[PersistGate] Not enough storage available, skipping cache rehydration.');
            return;
          }

          await initializeCache(dispatch, reducers);
        } catch (error) {
          const errorInfo = deviceErrorUtils.analyzeDeviceError(error);

          console.error('[PersistGate] Failed during initialization or storage check:', error);

          if (errorInfo.isDeviceError) {
            console.warn(`[PersistGate] Device limitation detected: ${errorInfo.category}`);
            if (errorInfo.details.detectionReason) {
              console.warn(`[PersistGate] Reason: ${errorInfo.details.detectionReason}`);
            }
          }

          if (errorInfo.shouldLogToSentry) {
            captureException(error, {
              contexts: {
                persistGate: {
                  operation: 'initializationOrCheck',
                  errorCategory: errorInfo.category,
                  ...errorInfo.details,
                },
              },
            });
          }
        }
      };

      const timeoutPromise = new Promise<void>((resolve) => {
        timeoutId = setTimeout(() => {
          console.warn(`[PersistGate] Rehydration timed out after ${TIMEOUT_MS}ms.`);
          resolve();
        }, TIMEOUT_MS);
      });

      Promise.race([initializationLogic(), timeoutPromise])
        .catch((error) => {
          const errorInfo = deviceErrorUtils.analyzeDeviceError(error);

          console.error('[PersistGate] Promise race error:', error);

          if (errorInfo.isDeviceError) {
            console.warn(`[PersistGate] Device limitation detected: ${errorInfo.category}`);
            if (errorInfo.details.detectionReason) {
              console.warn(`[PersistGate] Reason: ${errorInfo.details.detectionReason}`);
            }
          }

          if (errorInfo.shouldLogToSentry) {
            captureException(error, {
              contexts: {
                persistGate: {
                  operation: 'promiseRace',
                  errorCategory: errorInfo.category,
                  ...errorInfo.details,
                },
              },
            });
          }
        })
        .finally(() => {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          setIsLoaded(true);
        });

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    },
    [dispatch, reducers]
  );

  return isLoaded ? <>{children}</> : loadingComponent || null;
}
